import React, { useState } from 'react';
import useKYCStore from '../../store/kycStore';
import { FiUser, FiHash, FiCreditCard, FiBriefcase, FiMapPin, FiChevronLeft, FiChevronRight, FiDollarSign } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import IconWrapper from '../print-steps/IconWrapper';
import { successToastStyle, errorToastStyle } from '../../utils/toastConfig';

const BankDetailsStep: React.FC = () => {
  const { formData, updateFormData, prevStep, nextStep } = useKYCStore();
  const [errors, setErrors] = useState({
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch_name: '',
    upi_id: '',
  });
  
  const [paymentMethod, setPaymentMethod] = useState<'bank' | 'upi' | 'both'>(
    formData.upi_id && formData.account_number ? 'both' : 
    formData.upi_id ? 'upi' : 'bank'
  );

  const validateForm = () => {
    const newErrors = {
      account_holder_name: '',
      account_number: '',
      ifsc_code: '',
      bank_name: '',
      branch_name: '',
      upi_id: '',
    };
    let isValid = true;

    // At least one payment method is required
    if ((paymentMethod === 'bank' || paymentMethod === 'both') && !formData.account_number) {
      if (paymentMethod === 'both' || !formData.upi_id) {
        newErrors.account_number = 'Account number is required';
        isValid = false;
      }
    }

    if ((paymentMethod === 'upi' || paymentMethod === 'both') && !formData.upi_id) {
      if (paymentMethod === 'both' || !formData.account_number) {
        newErrors.upi_id = 'UPI ID is required';
        isValid = false;
      }
    }

    // If no payment method provided
    if (!formData.account_number && !formData.upi_id) {
      newErrors.account_number = 'You must provide either bank account details or UPI ID';
      newErrors.upi_id = 'You must provide either bank account details or UPI ID';
      isValid = false;
    }

    // Validate bank details only if bank payment method is selected
    if (paymentMethod === 'bank' || paymentMethod === 'both') {
      // Account holder name validation
      if (!formData.account_holder_name?.trim()) {
        newErrors.account_holder_name = 'Account holder name is required';
        isValid = false;
      }

      // Account number validation
      if (!formData.account_number?.trim()) {
        newErrors.account_number = 'Account number is required';
        isValid = false;
      } else if (!/^\d+$/.test(formData.account_number)) {
        newErrors.account_number = 'Account number should contain only digits';
        isValid = false;
      }

      // IFSC code validation
      if (!formData.ifsc_code?.trim()) {
        newErrors.ifsc_code = 'IFSC code is required';
        isValid = false;
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code)) {
        newErrors.ifsc_code = 'Enter a valid IFSC code (Format: AAAA0XXXXXX)';
        isValid = false;
      }

      // Bank name validation
      if (!formData.bank_name?.trim()) {
        newErrors.bank_name = 'Bank name is required';
        isValid = false;
      }

      // Branch name validation
      if (!formData.branch_name?.trim()) {
        newErrors.branch_name = 'Branch name is required';
        isValid = false;
      }
    }

    // Validate UPI ID only if UPI payment method is selected
    if (paymentMethod === 'upi' || paymentMethod === 'both') {
      if (!formData.upi_id?.trim()) {
        newErrors.upi_id = 'UPI ID is required';
        isValid = false;
      } else if (!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+$/.test(formData.upi_id)) {
        newErrors.upi_id = 'Enter a valid UPI ID (e.g., name@ybl)';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      toast.success('Payment details saved!', successToastStyle);
      nextStep();
    } else {
      toast.error('Please correct the errors in the form', errorToastStyle);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let updatedValue = value;
    
    // Format IFSC code to uppercase
    if (name === 'ifsc_code') {
      updatedValue = value.toUpperCase();
    }
    
    updateFormData({ [name]: updatedValue });
  };

  const handlePaymentMethodChange = (method: 'bank' | 'upi' | 'both') => {
    setPaymentMethod(method);
    
    // Clear errors when switching payment methods
    setErrors({
      account_holder_name: '',
      account_number: '',
      ifsc_code: '',
      bank_name: '',
      branch_name: '',
      upi_id: '',
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Payment Details</h2>
      
      {/* Payment Method Selection */}
      <div className="mb-6">
        <p className="block text-sm font-medium text-gray-700 mb-2">
          Select Payment Method <span className="text-red-500">*</span>
        </p>
        <div className="flex flex-wrap gap-4">
          <button
            type="button"
            onClick={() => handlePaymentMethodChange('bank')}
            className={`px-4 py-2 rounded-md border ${
              paymentMethod === 'bank' 
                ? 'bg-blue-50 border-blue-500 text-blue-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Bank Account Only
          </button>
          <button
            type="button"
            onClick={() => handlePaymentMethodChange('upi')}
            className={`px-4 py-2 rounded-md border ${
              paymentMethod === 'upi' 
                ? 'bg-blue-50 border-blue-500 text-blue-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            UPI Only
          </button>
          <button
            type="button"
            onClick={() => handlePaymentMethodChange('both')}
            className={`px-4 py-2 rounded-md border ${
              paymentMethod === 'both' 
                ? 'bg-blue-50 border-blue-500 text-blue-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Both Methods
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          You must provide at least one payment method for receiving payments.
        </p>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* UPI ID */}
          {(paymentMethod === 'upi' || paymentMethod === 'both') && (
            <div>
              <label htmlFor="upi_id" className="block text-sm font-medium text-gray-700 mb-1">
                UPI ID <span className="text-red-500">*</span>
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <IconWrapper icon={FiDollarSign} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="upi_id"
                  name="upi_id"
                  value={formData.upi_id || ''}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                    errors.upi_id ? 'border-red-300' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter UPI ID (e.g., name@ybl)"
                />
              </div>
              {errors.upi_id && <p className="mt-1 text-sm text-red-600">{errors.upi_id}</p>}
              <p className="mt-1 text-xs text-gray-500">
                Format: username@provider (e.g., johndoe@okicici)
              </p>
            </div>
          )}

          {/* Bank Account Details Section */}
          {(paymentMethod === 'bank' || paymentMethod === 'both') && (
            <>
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Bank Account Details</h3>
              </div>
              
              {/* Account Holder Name */}
              <div>
                <label htmlFor="account_holder_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name <span className="text-red-500">*</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IconWrapper icon={FiUser} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="account_holder_name"
                    name="account_holder_name"
                    value={formData.account_holder_name || ''}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                      errors.account_holder_name ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter account holder's name"
                  />
                </div>
                {errors.account_holder_name && <p className="mt-1 text-sm text-red-600">{errors.account_holder_name}</p>}
              </div>

              {/* Account Number */}
              <div>
                <label htmlFor="account_number" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number <span className="text-red-500">*</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IconWrapper icon={FiHash} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="account_number"
                    name="account_number"
                    value={formData.account_number || ''}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                      errors.account_number ? 'border-red-300' : 'border-gray-300'
                    } rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter account number"
                  />
                </div>
                {errors.account_number && <p className="mt-1 text-sm text-red-600">{errors.account_number}</p>}
              </div>

              {/* IFSC Code */}
              <div>
                <label htmlFor="ifsc_code" className="block text-sm font-medium text-gray-700 mb-1">
                  IFSC Code <span className="text-red-500">*</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <IconWrapper icon={FiCreditCard} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="ifsc_code"
                    name="ifsc_code"
                    value={formData.ifsc_code || ''}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                      errors.ifsc_code ? 'border-red-300' : 'border-gray-300'
                    } rounded-md uppercase focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                    placeholder="Enter IFSC code"
                  />
                </div>
                {errors.ifsc_code && <p className="mt-1 text-sm text-red-600">{errors.ifsc_code}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Format: AAAA0XXXXXX (e.g., SBIN0000123)
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Bank Name */}
                <div>
                  <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Bank Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IconWrapper icon={FiBriefcase} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="bank_name"
                      name="bank_name"
                      value={formData.bank_name || ''}
                      onChange={handleChange}
                      className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                        errors.bank_name ? 'border-red-300' : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="Enter bank name"
                    />
                  </div>
                  {errors.bank_name && <p className="mt-1 text-sm text-red-600">{errors.bank_name}</p>}
                </div>

                {/* Branch Name */}
                <div>
                  <label htmlFor="branch_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Branch Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IconWrapper icon={FiMapPin} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="branch_name"
                      name="branch_name"
                      value={formData.branch_name || ''}
                      onChange={handleChange}
                      className={`block w-full pl-10 pr-3 py-2 sm:text-sm border ${
                        errors.branch_name ? 'border-red-300' : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                      placeholder="Enter branch name"
                    />
                  </div>
                  {errors.branch_name && <p className="mt-1 text-sm text-red-600">{errors.branch_name}</p>}
                </div>
              </div>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            <button
              type="button"
              onClick={prevStep}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <IconWrapper icon={FiChevronLeft} className="-ml-1 mr-2 h-4 w-4" />
              Back
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next Step
              <IconWrapper icon={FiChevronRight} className="-mr-1 ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BankDetailsStep;